// WHO AN OUTREACH EMAIL SAYS HELLO TO — one rule, used by every writer.
//
// Why this exists (2026-10-02). The owner reported initial-outreach emails
// opening "Hi there," or "Hi <Company>," when they should open with the
// contact's first name. Every recent batch had them, and every one traced to
// the same two faults:
//   1. The receipt stub carries the row's `contact_name` but not its packed
//      people, so the draft payload could never see that j.kly@foxvalleyco.com
//      IS Jacob Klyachenko's address. A weak "does the address contain a
//      3-letter piece of the name" test then failed for every initial-and-
//      surname address (j.kly@, edmar@ = ED-ward MAR-ciszewski), the name was
//      withheld, and the model either wrote "Hi there," or guessed a name from
//      the address ("Hi Edmar,").
//   2. A name that leads with an initial ("J. David Rauch") went to the model
//      whole, and it greeted the whole thing; the template's own first-word
//      test rejected "J." and fell back to "Hi there,".
//
// The rule, in order (greetingFor):
//   1. the person the rep PICKED on the card's recipient dropdown;
//   2. a person on the row whose name TIES to the recipient address — full
//      name or surname in it, initials, first-letters + surname, prefix +
//      prefix, a nickname (mike@ is Michael, joeh@ is Joe H.) — greeted in the
//      form the address uses ("Mike", not "Michael");
//   3. an address that is plainly ANOTHER person's first name (beth@,
//      mallory@): that person, because the mailbox is theirs;
//   4. the person the data lists as the owner of that exact address;
//   5. a shared or company mailbox (info@, estimating@, brahmamasonry@): the
//      row's principal contact;
//   6. nobody at all: "Hi there,".
// It never greets a company: a "name" made of company words is not a person,
// and a company word in the address is never read as somebody's first name.
// "Hi there," survives only when no person can be tied to the address — the
// safety the old rule existed for ("Hi Yvonne" to tdluzak@ costs the reply)
// is kept, it simply stops firing on addresses that plainly belong to the
// person on the row.
//
// Pure: no imports, no I/O, so node tests pin it (scripts/test-greeting.mjs)
// and the template (outreachDraft.js), the draft payload (runHost.js) and the
// receipt stub (chat.js) all call the same function.

const clean = (v) => String(v ?? '').replace(/\s+/g, ' ').trim()

// Lowercase ASCII letters only, accents folded ("Dariusz" -> "dariusz",
// "José" -> "jose").
function letters(v) {
  return String(v ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z]/g, '')
}

const HONORIFIC = new Set(['mr', 'mrs', 'ms', 'miss', 'mx', 'dr', 'sir', 'madam', 'rev', 'prof'])
const SUFFIX = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v', 'phd', 'pe', 'esq', 'cpa'])
const JOINER = new Set(['and', 'or', 'the', 'of'])

// Words that make a "name" a company, a department or a mailbox — never a
// person to greet ("EDMAR Corporation", "Estimating Department").
// Trade words that are also surnames ("Paul Stone", "Ann Mason") are NOT
// here: only words no person's name is made of.
const NOT_A_PERSON = new Set([
  'inc', 'llc', 'llp', 'lp', 'ltd', 'corp', 'corporation', 'co', 'company', 'companies', 'group',
  'enterprises', 'enterprise', 'holdings', 'industries', 'partners', 'associates', 'services',
  'solutions', 'systems', 'masonry', 'construction', 'constructors', 'contracting',
  'contractors', 'contractor', 'builders', 'concrete', 'roofing', 'restoration', 'tuckpointing',
  'development', 'department', 'dept', 'office', 'team', 'sales', 'info', 'estimating',
  'estimator', 'estimators', 'estimates', 'accounting', 'admin', 'support', 'unknown',
  'unverified', 'none', 'na', 'owner', 'manager', 'president',
])

// Mailboxes that are nobody's own: the row's principal is the right person to
// greet at one of these.
const MAILBOX = new Set([
  'office', 'info', 'information', 'sales', 'admin', 'administrator', 'contact', 'contacts',
  'estimating', 'estimator', 'estimators', 'estimate', 'estimates', 'bid', 'bids', 'bidding',
  'accounting', 'accounts', 'ap', 'ar', 'billing', 'invoices', 'invoice', 'hello', 'hi', 'team',
  'support', 'service', 'services', 'mail', 'email', 'inbox', 'quote', 'quotes', 'projects',
  'project', 'jobs', 'hr', 'careers', 'marketing', 'dispatch', 'main', 'general', 'inquiries',
  'inquiry', 'enquiries', 'help', 'orders', 'purchasing', 'operations', 'ops', 'frontdesk',
  'reception', 'receptionist', 'webmaster', 'noreply', 'postmaster', 'payroll', 'mgmt',
  'management', 'company', 'corp', 'masonry', 'construction', 'concrete', 'roofing', 'admin1',
])

// nickname -> the given names it is short for. Only the forms people actually
// put in an email address; a prefix of three letters or more ("chris",
// "stan", "doug") is recognised without being listed.
const NICKNAMES = {
  al: ['albert', 'alan', 'allen', 'alfred', 'alfonso', 'alberto', 'alvin'],
  abe: ['abraham'],
  alex: ['alexander', 'alejandro', 'alexis', 'alexandra'],
  andy: ['andrew', 'andres', 'andreas'],
  drew: ['andrew'],
  tony: ['anthony', 'antonio', 'antoni'],
  art: ['arthur', 'arturo'],
  ben: ['benjamin', 'bennett', 'benedict'],
  benny: ['benjamin', 'benito'],
  bert: ['albert', 'robert', 'herbert', 'bertram'],
  bill: ['william'],
  billy: ['william'],
  will: ['william'],
  willie: ['william'],
  bob: ['robert'],
  bobby: ['robert'],
  rob: ['robert', 'roberto'],
  robbie: ['robert'],
  brad: ['bradley', 'bradford'],
  cathy: ['catherine', 'cathleen'],
  kathy: ['katherine', 'kathleen', 'kathryn'],
  kate: ['katherine', 'kathleen', 'kathryn', 'katarzyna'],
  katie: ['katherine', 'kathleen', 'kathryn'],
  chuck: ['charles'],
  charlie: ['charles'],
  chip: ['charles'],
  chet: ['chester'],
  dan: ['daniel'],
  danny: ['daniel'],
  dave: ['david', 'dawid'],
  davey: ['david'],
  deb: ['deborah', 'debra'],
  debbie: ['deborah', 'debra'],
  dick: ['richard'],
  rich: ['richard', 'ricardo'],
  rick: ['richard', 'ricardo', 'frederick', 'eric', 'rickey'],
  ricky: ['richard', 'ricardo'],
  don: ['donald'],
  donnie: ['donald'],
  ed: ['edward', 'edwin', 'edmund', 'eduardo', 'edgar'],
  eddie: ['edward', 'edwin', 'eduardo', 'edgar'],
  ted: ['edward', 'theodore'],
  teddy: ['edward', 'theodore'],
  frank: ['francis', 'francisco', 'franklin', 'franciszek'],
  fran: ['frances', 'francis', 'francisco'],
  kiko: ['francisco'],
  paco: ['francisco'],
  pancho: ['francisco'],
  fred: ['frederick', 'alfred', 'fredrick', 'frederic'],
  freddie: ['frederick', 'alfred'],
  gabe: ['gabriel'],
  gene: ['eugene'],
  greg: ['gregory', 'gregg', 'grzegorz'],
  hank: ['henry'],
  harry: ['harold', 'henry'],
  herb: ['herbert'],
  jack: ['john', 'jackson', 'jacob'],
  jake: ['jacob'],
  jim: ['james'],
  jimmy: ['james'],
  jamie: ['james'],
  jeff: ['jeffrey', 'jeffery', 'jefferson', 'geoffrey'],
  jen: ['jennifer'],
  jenny: ['jennifer'],
  jerry: ['gerald', 'jerome', 'jeremiah', 'gerard'],
  joe: ['joseph', 'jose', 'josef', 'jozef'],
  joey: ['joseph'],
  johnny: ['john', 'jonathan'],
  jon: ['jonathan', 'john'],
  josh: ['joshua'],
  ken: ['kenneth'],
  kenny: ['kenneth'],
  larry: ['lawrence', 'laurence'],
  len: ['leonard'],
  lenny: ['leonard'],
  les: ['leslie', 'lester'],
  liz: ['elizabeth'],
  beth: ['elizabeth', 'bethany'],
  betty: ['elizabeth'],
  betsy: ['elizabeth'],
  lou: ['louis', 'luis'],
  mandy: ['amanda'],
  marty: ['martin'],
  matt: ['matthew', 'mateo', 'mateusz'],
  max: ['maxwell', 'maximilian', 'maximo'],
  meg: ['margaret', 'megan'],
  peggy: ['margaret'],
  mel: ['melvin', 'melissa'],
  mike: ['michael', 'miguel', 'michal', 'mikael'],
  mikey: ['michael'],
  mick: ['michael'],
  mitch: ['mitchell'],
  nate: ['nathan', 'nathaniel'],
  nick: ['nicholas', 'nicolas', 'nicola', 'nikolai'],
  pat: ['patrick', 'patricia'],
  patty: ['patricia'],
  pete: ['peter', 'pedro', 'piotr'],
  phil: ['phillip', 'philip'],
  ray: ['raymond', 'ramon'],
  ron: ['ronald', 'ronaldo'],
  ronnie: ['ronald'],
  russ: ['russell'],
  sam: ['samuel', 'samantha'],
  sandy: ['sandra'],
  sal: ['salvatore', 'salvador'],
  steve: ['steven', 'stephen', 'esteban'],
  stan: ['stanley', 'stanislaw'],
  sue: ['susan', 'suzanne'],
  susie: ['susan'],
  terry: ['terrence', 'terence', 'teresa'],
  tim: ['timothy'],
  tom: ['thomas', 'tomas', 'tomasz'],
  tommy: ['thomas'],
  vic: ['victor', 'vicente'],
  vince: ['vincent', 'vincenzo'],
  walt: ['walter'],
  wes: ['wesley'],
  zach: ['zachary'],
  zack: ['zachary'],
  chente: ['vicente'],
  memo: ['guillermo'],
  pepe: ['jose'],
  nacho: ['ignacio'],
  lalo: ['eduardo'],
  beto: ['alberto', 'roberto', 'humberto'],
  chuy: ['jesus'],
  lupe: ['guadalupe'],
  manny: ['manuel'],
  rudy: ['rudolph', 'rodolfo'],
}

// Given names common enough that an address made of one is that person's
// own mailbox (beth@, mallory@, brenda@). Only consulted when no person on
// the row ties to the address — so it decides between "this is somebody
// else's mailbox, greet them" and "fall back to the row's own data". The
// list leans on the people this book actually holds: US, Hispanic, Polish
// and Italian given names.
const FIRST_NAMES = new Set(
  `aaron abby abel abigail adam adela adalberto adolfo adrian adriana agnieszka agustin al alan albert alberto
  alejandra alejandro alex alexander alfonso alfred alfredo ali alice alicia alison allen allison alma alonso
  alvaro alvin alyssa amanda amber amir amy ana andre andrea andres andrew andrzej andy angel angela angelo
  angie ann anna anne annette annie anthony antonio april araceli armando arnold art artur arthur arturo ashley
  audrey austin barbara barry bartosz beata beatriz becky belinda ben benito benjamin bernard beth betty beverly
  bill billy blake blanca bob bobby bogdan bonnie boris brad bradley brandon brenda brent brett brian brittany
  brooke bruce bryan byron cal calvin cameron candace carl carla carlo carlos carmen carol carole caroline
  carolyn carrie casey cassandra catalina catherine cathy cecil cecilia cesar chad charlene charles charlie
  charlotte chase cheryl chester chip chris christian christina christine christopher christy chuck cindy
  claire clarence clark claude claudia clay clayton cliff clifford clint clinton clyde cody colin colleen
  connie connor consuelo corey courtney craig cristian crystal curtis cynthia czeslaw daisy dale dallas
  dan dana daniel danielle danny dariusz darlene darrell darren darryl dave david dawn dean deb debbie
  deborah debra denise dennis derek derrick devin dewey diana diane dianne dick diego dino dmitri dolores
  don donald donna donnie dora doris dorota dorothy doug douglas drew duane dustin dwayne dwight earl ed
  eddie edgar edith eduardo edward edwin efrain eileen elaine eleanor elena eli elias elijah elizabeth ellen
  elmer elsa elzbieta emilio emily emma emmett enrique enzo eric erica erik erin ernest ernesto esperanza
  estela esteban esther ethan eugene eugenio eva evan evelyn everett ewa ezequiel fabiola faith federico
  felipe felix fernando floyd francis francisco franco frank franklin fred freddie frederick gabriel gabriela
  gail gary gene geoffrey george gerald gerardo gilbert gilberto gina gino giovanni giuseppe gladys glen
  glenn gloria gonzalo gordon grace graciela grady graham grant grazyna greg gregg gregory grzegorz
  guadalupe guillermo gustavo guy hal halina hank hannah harold harrison harry harvey hassan heather hector
  heidi helen henry henryk herbert heriberto herman hilda holly howard hugh hugo humberto hunter ian igor
  ignacio irena irene irma isaac isabel isidro ismael ivan jacek jack jackie jackson jacob jacqueline
  jadwiga jaime jake jakub james jamie jan jane janet janice janusz jared jaroslaw jasmine jason javier
  jay jean jeanne jeff jeffery jeffrey jen jennifer jenny jeremiah jeremy jerome jerry jerzy jesse jessica
  jesus jill jim jimmy joan joann joanna joanne joaquin jodi joe joel joey john johnny jon jonathan jordan
  jorge jose josefina joseph josephine josh joshua josue jozef joy joyce juan juana judith judy julia julian
  julie julio june justin kamil karen kari karla katarzyna kate katherine kathleen kathryn kathy katie kay
  kayla kazimierz keith kelly ken kenneth kenny kent kevin kim kimberly kirk krisztina kristen kristin
  kristina krystyna krzysztof kurt kyle lance larry laura lauren laurie lawrence leah lee lena leo leon
  leonard leonardo leonel leroy leslie lester leszek leticia lewis linda lindsay lisa lloyd logan lois
  lonnie lorenzo loretta lori lorraine louis louise lourdes lucas lucia lucy luigi luis lukasz luke luz
  lyle lynn mack magdalena malcolm malgorzata mallory mandy manuel marc marcela marcelino marcia marcin
  marco marcos marcus marek margaret margarita maria marian marie marilyn mario marion mariusz marjorie
  mark marlene marshall marta martha martin marty marvin mary massimo mateo matt matteo matthew maureen
  mauricio maurice max maxwell megan melanie melissa melvin mercedes micah michael michal michele michelle
  mickey miguel mike mildred miles milton miriam miroslaw missy mitch mitchell mohammed moises molly monica
  monika morgan muhammad nancy natalie nathan nathaniel neal neil nelson nestor nicholas nick nicole nina
  nino noah nora norma norman octavio ofelia oleg olga oliver olivia omar orlando orville oscar osvaldo otis
  owen pablo paige pam pamela paolo pat patricia patrick patty paul paula pauline pawel pedro peggy penny
  perry pete peter phil philip phillip phyllis pietro piotr preston przemyslaw quentin rachel rafael rafal
  raj ralph ramiro ramon randall randy raquel raul ravi ray raymond rebecca refugio reggie reginald regina
  renata renee rex reynaldo rhonda ricardo rick ricky riley rita rob robbie robert roberta roberto robin
  rocco rocio rod rodney rodolfo rodrigo rogelio roger roland rolando roman ron ronald ronnie rosa rosario
  rose rosemary ross roy ruben rudy russ russell ruth ryan ryszard sabrina sal sally salvador salvatore sam
  samantha samuel sandra sandy sanjay santiago santos sara sarah saul scott sean sergei sergio seth shane
  shannon sharon shaun shawn sheila shelby sheldon shelly sherry shirley sidney silvia slawomir socorro
  sofia sonia sophia spencer stacey stacy stan stanislaw stanley stefan stephanie stephen steve steven
  stewart stuart sue susan suzanne sylvia szymon tadeusz tamara tammy tanya tara ted teodoro teresa terri
  terrance terrence terry theodore theresa thomas tiffany tim timothy tina toby todd tom tomas tomasz tommy
  tony tracey tracy travis trent trevor troy ty tyler tyrone ulises urszula valerie vance vanessa vera
  vernon veronica vicente vicki victor victoria viktor vijay vince vincent vincenzo virginia vito vivian
  vladimir wade waldemar walker wallace walt walter wanda warren wayne wendell wendy wes wesley whitney
  wieslaw wilfredo will william willie wilson witold wojciech xavier ximena yolanda yuri yvonne zach zachary
  zbigniew zofia`.split(/\s+/).filter(Boolean),
)

/**
 * The people in a row's packed contacts blob ("name=…; apollo_email=…;"),
 * as {name, email}. The same reading as lib/leadSearch.js#parsePackedContacts,
 * kept here so the template (outreachDraft.js) can use the rule without
 * importing the search module and everything it pulls in.
 */
export function packedPeople(raw) {
  const out = []
  let cur = null
  for (const tok of String(raw ?? '').split(';')) {
    const eq = tok.indexOf('=')
    if (eq === -1) continue
    const key = tok.slice(0, eq).trim()
    const val = tok.slice(eq + 1).trim()
    if (key === 'name') {
      cur = { name: val, email: '' }
      out.push(cur)
    } else if (cur && (key === 'apollo_email' || (key === 'email' && !cur.email))) {
      cur.email = val
    }
  }
  return out.filter((p) => p.name)
}

/** "o'brien" -> "O'Brien", "mary-jo" -> "Mary-Jo", "WAYNE" -> "Wayne". */
function displayCase(token) {
  const t = clean(token)
  if (!t) return ''
  const mixed = /[a-z]/.test(t) && /[A-Z]/.test(t)
  if (mixed) return t
  return t
    .toLowerCase()
    .replace(/(^|[-'’])([a-zà-ɏ])/g, (_, sep, ch) => sep + ch.toUpperCase())
}

/**
 * A person's name read into parts: `given` (the given names in order, with
 * honorifics, suffixes, joiners and bare initials dropped), `initials` (every
 * name's first letter, in order, initials included), `last` (the surname) and
 * `first` (the given name to greet, display-cased). "Kuerbis, Stanley S." is
 * read as Stanley S. Kuerbis.
 */
export function nameParts(fullName) {
  let raw = clean(fullName).replace(/\s*\([^)]*\)\s*/g, ' ')
  // "Last, First Middle" — one comma, with a single-word surname before it.
  const comma = raw.split(',')
  if (comma.length === 2 && clean(comma[0]) && !/\s/.test(clean(comma[0])) && clean(comma[1])) {
    const after = letters(comma[1])
    if (!SUFFIX.has(after)) raw = `${clean(comma[1])} ${clean(comma[0])}`
  }
  const tokens = raw
    .split(/[\s,&/+]+/)
    .map((t) => t.replace(/^[^A-Za-zÀ-ɏ]+|[^A-Za-zÀ-ɏ.'’-]+$/g, ''))
    .filter(Boolean)
  const words = []
  const initials = []
  for (const tok of tokens) {
    const l = letters(tok)
    if (!l) continue
    if (HONORIFIC.has(l) || SUFFIX.has(l) || JOINER.has(l)) continue
    initials.push(l[0])
    if (l.length < 2 || /^[A-Za-z]\.$/.test(tok)) continue // a bare initial: "J." / "S"
    words.push(tok.replace(/\.$/, ''))
  }
  if (!words.length) return { given: [], initials, last: '', first: '' }
  const last = words.length > 1 ? letters(words[words.length - 1]) : ''
  const given = (words.length > 1 ? words.slice(0, -1) : words).map(letters)
  return { given, initials, last, first: displayCase(words[0]) }
}

/**
 * Is this a person we can greet? A company, a department, a mailbox word, an
 * address, or the address's own local part handed back as a "name" (the CRM
 * rows pack `name=jdoe; apollo_email=jdoe@x.com`) is not.
 */
export function isPersonName(name, email = '') {
  const n = clean(name)
  if (!n || n.includes('@') || /\d/.test(n)) return false
  const toks = n.toLowerCase().split(/[\s,&/.+-]+/).map(letters).filter(Boolean)
  if (!toks.length) return false
  if (toks.some((t) => NOT_A_PERSON.has(t))) return false
  const local = letters(String(email || '').split('@')[0])
  if (toks.length === 1 && local && toks[0] === local) return false
  return Boolean(nameParts(n).first)
}

/** The first name to greet for a full name, '' when there is none. */
export function firstNameOf(fullName) {
  return isPersonName(fullName) ? nameParts(fullName).first : ''
}

function localOf(email) {
  const local = clean(email).toLowerCase().split('@')[0] || ''
  const bare = letters(local.replace(/\d+/g, ''))
  const tokens = local
    .split(/[._+\-\d]+/)
    .map(letters)
    .filter(Boolean)
  return { local, bare, tokens }
}

// Is `token` this given name, a nickname of it, or a prefix of it of three
// letters or more ("chris" for christopher)? Returns the matched form.
function nicknameMatch(token, given) {
  if (!token || !given) return ''
  if (token === given) return token
  if ((NICKNAMES[token] || []).includes(given)) return token
  if (token.length >= 3 && given.startsWith(token)) return token
  return ''
}

// A given name or a KNOWN nickname of it, followed by one initial ("mikeb@",
// "joeh@"). Not a mere prefix: "lynn@" is not "lyn" + n for Lyndell, it is
// Lynn. Returns the name part, or ''.
function nameAndInitial(token, given) {
  if (!token || token.length < 3 || !given) return ''
  const part = token.slice(0, -1)
  if (part === given || (NICKNAMES[part] || []).includes(given)) return part
  return ''
}

/**
 * Does this address belong to this person? Every shape a company mail system
 * actually builds an address from a name:
 *   - the full given name, or the surname, at the start or end of the local
 *     part ("chris@", "rkendall@", "drauch@", "chorn@"), or a long piece of
 *     either anywhere in it;
 *   - a nickname or a 3+-letter prefix of the given name, optionally with one
 *     trailing initial ("mike@" Michael, "joeh@" Joseph Heck, "mikeb@");
 *   - the initials, with a Jr./Sr. tail allowed ("ag@", "lh7421@", "wbjr@");
 *   - a prefix of a given name followed by a prefix of the surname, or the
 *     reverse ("j.kly@" Jacob Klyachenko, "edmar@" Edward Marciszewski), with
 *     a two-letter slip allowed after a long surname piece ("rgutierres52@"
 *     for Ramon Gutierrez).
 */
export function tiesTo(email, fullName) {
  if (!isPersonName(fullName)) return false
  const { bare, tokens } = localOf(email)
  if (!bare) return false
  const { given, initials, last } = nameParts(fullName)
  const names = [...given, ...(last ? [last] : [])].filter((t) => t.length >= 2)
  for (const t of names) {
    if (t.length >= 3 && (bare.startsWith(t) || bare.endsWith(t))) return true
    if (t.length >= 5 && bare.includes(t)) return true
  }
  const head = tokens[0] || ''
  for (const g of given) {
    if (nicknameMatch(head, g)) return true
    if (nameAndInitial(head, g)) return true
  }
  const ini = initials.join('')
  const bareNoSuffix = bare.replace(/(jr|sr)$/, '')
  if (ini.length >= 2 && (bareNoSuffix === ini || bareNoSuffix === ini[0] + ini[ini.length - 1])) return true
  if (last && bare.length >= 3) {
    for (const g of given) {
      for (let i = 1; i <= g.length; i += 1) {
        for (let j = 2; j <= last.length; j += 1) {
          const forward = g.slice(0, i) + last.slice(0, j)
          const backward = last.slice(0, j) + g.slice(0, i)
          if (bare === forward || bare === backward) return true
          if (j >= 4 && bare.length - forward.length <= 2 && bare.length > forward.length && bare.startsWith(forward)) return true
        }
      }
    }
  }
  return false
}

// The form to greet a tied person by: the nickname the address itself uses
// when it uses one ("mike@" -> "Mike" for Michael), the person's own first
// name otherwise.
function addressForm(email, fullName) {
  const { tokens } = localOf(email)
  const head = tokens[0] || ''
  const { given, first } = nameParts(fullName)
  // The address IS one of their given names — a quoted nickname on the row
  // ('Ulrich "Uli" Dargel' at uli@) or a middle name they go by.
  if (head.length >= 2 && given.slice(1).includes(head)) return displayCase(head)
  const g = given[0] || ''
  const form = nicknameMatch(head, g) || nameAndInitial(head, g)
  if (form && form.length >= 2 && form !== g) return displayCase(form)
  return first
}

/**
 * WHO TO GREET. See the rule at the top of this file.
 *
 * @param {object}   input
 * @param {string}   input.email        the address this email is going to
 * @param {string}   input.contactName  the row's named contact
 * @param {Array<{name: string, email?: string}>} input.people  the row's packed people
 * @param {string}   input.company      the company's name
 * @param {string}   input.website      the company's site (its domain is a company word source)
 * @param {{name?: string, email?: string}|null} input.picked  the rep's own pick
 * @returns {{ name: string, person: string, how: 'picked'|'owner'|'listed'|'address'|'mailbox'|'none' }}
 *   `name` is the first name for "Hi <name>," ('' -> "Hi there,"), `person`
 *   the full name it belongs to ('' when only the address names them).
 */
export function greetingFor({ email = '', contactName = '', people = [], company = '', website = '', picked = null } = {}) {
  const to = clean(picked?.email || email).toLowerCase()
  if (picked && isPersonName(picked.name)) {
    const pickedName = clean(picked.name)
    return {
      name: to && tiesTo(to, pickedName) ? addressForm(to, pickedName) : firstNameOf(pickedName),
      person: pickedName,
      how: 'picked',
    }
  }
  const contact = clean(contactName)
  const folks = []
  const seen = new Set()
  for (const p of Array.isArray(people) ? people : []) {
    const name = clean(p?.name)
    const addr = clean(p?.email).toLowerCase()
    if (!isPersonName(name, addr)) continue
    const k = `${letters(name)}|${addr}`
    if (seen.has(k)) continue
    seen.add(k)
    folks.push({ name, email: addr })
  }
  if (isPersonName(contact) && !folks.some((p) => letters(p.name) === letters(contact))) {
    folks.push({ name: contact, email: '' })
  }
  if (!to || !to.includes('@')) {
    const who = isPersonName(contact) ? contact : folks[0]?.name || ''
    return who ? { name: firstNameOf(who), person: who, how: 'listed' } : { name: '', person: '', how: 'none' }
  }
  const owner = folks.find((p) => p.email === to) || null
  // 2. a person whose name ties to the address — the owner first, then the
  //    row's own contact, then everyone else in the row's order.
  const order = [
    ...(owner ? [owner] : []),
    ...folks.filter((p) => p !== owner && letters(p.name) === letters(contact)),
    ...folks.filter((p) => p !== owner && letters(p.name) !== letters(contact)),
  ]
  const tied = order.find((p) => tiesTo(to, p.name))
  if (tied) {
    return { name: addressForm(to, tied.name), person: tied.name, how: tied === owner ? 'owner' : 'listed' }
  }
  // 3. an address that is plainly somebody's own first name.
  const { tokens, bare } = localOf(to)
  const head = tokens[0] || ''
  const domainLabel = letters(to.split('@')[1]?.split('.')[0] || '')
  const companyBlob = `${letters(company)} ${letters(String(website || '').replace(/^https?:\/\//, '').split('/')[0].split('.').slice(-2, -1)[0] || '')} ${domainLabel}`
  const surnames = new Set(folks.map((p) => nameParts(p.name).last).filter(Boolean))
  if (
    head.length >= 3 &&
    head.length <= 12 &&
    (FIRST_NAMES.has(head) || Object.prototype.hasOwnProperty.call(NICKNAMES, head)) &&
    !MAILBOX.has(head) &&
    !companyBlob.includes(head) &&
    !surnames.has(head)
  ) {
    return { name: displayCase(head), person: '', how: 'address' }
  }
  // 4. the person the data lists as this address's owner.
  if (owner) return { name: firstNameOf(owner.name), person: owner.name, how: 'owner' }
  // 5. a shared or company mailbox: the row's principal.
  const companyish =
    MAILBOX.has(head) ||
    MAILBOX.has(bare) ||
    (bare.length >= 5 && (companyBlob.includes(bare) || letters(company).includes(bare.slice(0, 6))))
  if (companyish) {
    const principal = isPersonName(contact) ? contact : folks[0]?.name || ''
    if (principal) return { name: firstNameOf(principal), person: principal, how: 'mailbox' }
  }
  return { name: '', person: '', how: 'none' }
}

// The greeting words a writer opens with.
const GREET_WORD = '(?:hi|hello|hey|dear|greetings|good\\s+(?:morning|afternoon|evening))'
// A line that is ONLY a greeting: one that ends with its comma ("Hi Jacob,",
// "Hi J. David Rauch,", "Hello EDMAR Corporation team,"), or a bare one of
// at most three words with no punctuation at all ("Hi Jacob", "Hi there").
// "Hi there — a note for Acme." is NOT one: it carries the email's first
// sentence, and replacing the line would delete it.
const GREETING_ONLY_COMMA = new RegExp(`^${GREET_WORD}\\b[^\\n,]{0,60},\\s*$`, 'i')
const GREETING_ONLY_BARE = new RegExp(`^${GREET_WORD}\\b(?:\\s+[A-Za-zÀ-ɏ'’-]+){0,3}\\s*$`, 'i')
// A greeting with the email after it on the same line, after a comma or a
// dash: "Hi Jacob, quick question…", "Hi there — a note…".
const GREETING_LEAD = /^(?:hi|hello|hey|dear)\b[^\n,.!?;:—–]{0,40}(?:,|\s+[—–-]\s+)\s*/i
const STARTS_WITH_GREETING = new RegExp(`^${GREET_WORD}\\b`, 'i')

/**
 * The email's greeting made to say exactly "Hi <name>," ("Hi there," when
 * there is no name). A writer — the model or a template — that greeted
 * anybody else, or the whole name, or the company, is corrected; an email
 * that opened without a greeting gets one as its own first paragraph. A first
 * line that starts like a greeting but cannot be read safely is left exactly
 * as written: a doubled or truncated opening is worse than an unchanged one.
 */
export function enforceGreeting(bodyText, name) {
  const text = String(bodyText ?? '')
  if (!text.trim()) return text
  const want = `Hi ${clean(name) || 'there'},`
  const lines = text.split('\n')
  const at = lines.findIndex((l) => l.trim())
  if (at < 0) return text
  const line = lines[at].trim()
  if (GREETING_ONLY_COMMA.test(line) || GREETING_ONLY_BARE.test(line)) {
    if (line === want) return text
    lines[at] = want
    return lines.join('\n')
  }
  const lead = line.match(GREETING_LEAD)
  if (lead) {
    lines[at] = `${want} ${line.slice(lead[0].length)}`.trim()
    return lines.join('\n')
  }
  if (STARTS_WITH_GREETING.test(line)) return text
  return `${want}\n\n${text}`
}
